import type { CompanyStage } from "@pinpoint/shared";
import { COMPANY_STAGE_ORDER } from "@pinpoint/shared";

/**
 * Stage presentation, defined once (docs/02 §1).
 *
 * The colour scale is not decorative — it maps to *where in the funnel* a
 * company is, cool through warm to green, so a glance down the pipeline table
 * reads as progress. Every screen imports from here so the same stage never
 * shows up in two different colours.
 */
export interface StageStyle {
  label: string;
  /** Tailwind classes for a filled chip. */
  chip: string;
  /** Solid colour for funnel bars and dots. */
  dot: string;
}

export const STAGE_STYLES: Record<CompanyStage, StageStyle> = {
  sourced: {
    label: "Sourced",
    chip: "bg-slate-500/15 text-slate-700 dark:text-slate-300 ring-slate-500/25",
    dot: "bg-slate-400",
  },
  qualified: {
    label: "Qualified",
    chip: "bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-blue-500/25",
    dot: "bg-blue-500",
  },
  enriching: {
    label: "Enriching",
    chip: "bg-violet-500/15 text-violet-700 dark:text-violet-300 ring-violet-500/25",
    dot: "bg-violet-500",
  },
  enriched: {
    label: "Enriched",
    chip: "bg-violet-500/15 text-violet-700 dark:text-violet-300 ring-violet-500/25",
    dot: "bg-violet-500",
  },
  drafting: {
    label: "Drafting",
    chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/25",
    dot: "bg-amber-500",
  },
  in_review: {
    label: "In review",
    chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/25",
    dot: "bg-amber-500",
  },
  approved: {
    label: "Approved",
    chip: "bg-green-500/15 text-green-700 dark:text-green-300 ring-green-500/25",
    dot: "bg-green-500",
  },
  sending: {
    label: "Sending",
    chip: "bg-teal-500/15 text-teal-700 dark:text-teal-300 ring-teal-500/25",
    dot: "bg-teal-500",
  },
  active: {
    label: "Active",
    chip: "bg-teal-500/15 text-teal-700 dark:text-teal-300 ring-teal-500/25",
    dot: "bg-teal-500",
  },
  replied: {
    label: "Replied",
    chip: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/25",
    dot: "bg-emerald-500",
  },
  converted: {
    label: "Converted",
    chip: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 ring-emerald-500/40",
    dot: "bg-emerald-600",
  },
  parked: {
    label: "Parked",
    chip: "bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/25",
    dot: "bg-rose-400",
  },
  killed: {
    label: "Killed",
    chip: "bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/25",
    dot: "bg-rose-500",
  },
};

export const FUNNEL_ORDER = COMPANY_STAGE_ORDER;

export const ALL_STAGES: CompanyStage[] = [
  ...COMPANY_STAGE_ORDER,
  "parked",
  "killed",
];

/**
 * What a human can move a company to from here.
 *
 * A curated subset of the legal transitions, not all of them: the stage machine
 * permits moves that only make sense to an agent mid-pipeline, and offering an
 * operator thirteen buttons would bury the three decisions they actually make.
 */
export function humanStageActions(stage: CompanyStage): CompanyStage[] {
  switch (stage) {
    case "sourced":
      return ["qualified", "parked", "killed"];
    case "qualified":
    case "enriching":
    case "enriched":
      return ["parked", "killed"];
    case "parked":
      return ["qualified", "killed"];
    case "killed":
      return ["qualified", "parked"];
    default:
      return ["parked", "killed"];
  }
}

/** Parking or killing needs a reason — it is a learning signal, not a status. */
export function actionNeedsReason(stage: CompanyStage): boolean {
  return stage === "parked" || stage === "killed";
}
